import type { DomainRuleMode } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@recipes/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminPage } from '@/features/admin/admin-layout';
import { FormField } from '@/features/shared/form-field';
import { formatDate } from '@/features/shared/labels';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

export function AdminScraperPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const stats = useQuery({
    queryKey: queryKeys.admin.scraperStats,
    queryFn: () => api.admin.scraperStats(),
  });
  const failures = useInfiniteQuery({
    queryKey: queryKeys.admin.scraperFailures({}),
    queryFn: ({ pageParam }) => api.admin.scraperFailures({ cursor: pageParam, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rules = useQuery({
    queryKey: queryKeys.admin.domainRules,
    queryFn: () => api.admin.domainRules(),
  });
  const [domain, setDomain] = useState('');
  const [mode, setMode] = useState<DomainRuleMode>('BLOCK');
  const [note, setNote] = useState('');
  const createRule = useMutation({
    mutationFn: () =>
      api.admin.createDomainRule({
        domain: domain.trim().toLowerCase(),
        mode,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.domainRules });
      setDomain('');
      setNote('');
    },
    onError: (e) => toastError(e),
  });
  const deleteRule = useMutation({
    mutationFn: (id: string) => api.admin.deleteDomainRule(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.admin.domainRules }),
    onError: (e) => toastError(e),
  });
  const failureRows = failures.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <AdminPage section="scraper">
      <div className="space-y-8">
        <section aria-labelledby="scraper-stats">
          <h2 id="scraper-stats" className="mb-2 text-lg font-semibold">
            {t('admin.scraper.stats')}
          </h2>
          {stats.isLoading && <Spinner label={t('app.loading')} />}
          {stats.error && <Alert variant="destructive">{describeError(stats.error)}</Alert>}
          {stats.data && (
            <div className="grid gap-4 md:grid-cols-2">
              <ResponsiveTable
                caption={t('admin.scraper.byStrategy')}
                rowKey={(r) => r.strategy}
                rows={stats.data.byStrategy}
                columns={[
                  { key: 'strategy', header: t('admin.scraper.strategy'), cell: (r) => r.strategy },
                  { key: 'success', header: t('admin.scraper.success'), cell: (r) => r.success },
                  { key: 'failed', header: t('admin.scraper.failed'), cell: (r) => r.failed },
                ]}
              />
              <ResponsiveTable
                caption={t('admin.scraper.byDomain')}
                rowKey={(r) => r.domain}
                rows={stats.data.byDomain}
                columns={[
                  { key: 'domain', header: t('admin.scraper.domain'), cell: (r) => r.domain },
                  { key: 'success', header: t('admin.scraper.success'), cell: (r) => r.success },
                  { key: 'failed', header: t('admin.scraper.failed'), cell: (r) => r.failed },
                ]}
              />
            </div>
          )}
        </section>

        <section aria-labelledby="scraper-rules">
          <h2 id="scraper-rules" className="mb-2 text-lg font-semibold">
            {t('admin.scraper.rules')}
          </h2>
          <form
            noValidate
            className="mb-3 grid gap-3 rounded-lg border p-3 sm:grid-cols-[1fr_9rem_1fr_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              if (domain.trim()) createRule.mutate();
            }}
          >
            <FormField label={t('admin.scraper.domain')} required>
              <Input
                value={domain}
                maxLength={253}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
              />
            </FormField>
            <div>
              <Label htmlFor="rule-mode">{t('admin.scraper.mode')}</Label>
              <Select value={mode} onValueChange={(v) => setMode(v as DomainRuleMode)}>
                <SelectTrigger id="rule-mode" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BLOCK">{t('admin.scraper.modes.BLOCK')}</SelectItem>
                  <SelectItem value="ALLOW">{t('admin.scraper.modes.ALLOW')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FormField label={t('admin.units.note')} optionalText={t('common.optional')}>
              <Input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
            </FormField>
            <Button type="submit" loading={createRule.isPending} disabled={!domain.trim()}>
              <Plus aria-hidden /> {t('admin.scraper.addRule')}
            </Button>
          </form>
          {rules.data && (
            <ResponsiveTable
              caption={t('admin.scraper.rules')}
              rowKey={(r) => r.id}
              rows={rules.data}
              empty={t('common.noResults')}
              columns={[
                {
                  key: 'domain',
                  header: t('admin.scraper.domain'),
                  cell: (r) => <span className="font-mono text-xs">{r.domain}</span>,
                },
                {
                  key: 'mode',
                  header: t('admin.scraper.mode'),
                  cell: (r) => (
                    <Badge variant={r.mode === 'BLOCK' ? 'destructive' : 'success'}>
                      {t(`admin.scraper.modes.${r.mode}`)}
                    </Badge>
                  ),
                },
                { key: 'note', header: t('admin.units.note'), cell: (r) => r.note ?? '–' },
                {
                  key: 'actions',
                  header: '',
                  label: t('admin.actions'),
                  cell: (r) => (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t('admin.scraper.deleteRule', { domain: r.domain })}
                      onClick={() => deleteRule.mutate(r.id)}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </section>

        <section aria-labelledby="scraper-failures">
          <h2 id="scraper-failures" className="mb-2 text-lg font-semibold">
            {t('admin.scraper.failures')}
          </h2>
          {failures.isLoading && <Spinner label={t('app.loading')} />}
          {failures.error && <Alert variant="destructive">{describeError(failures.error)}</Alert>}
          {failures.data && (
            <ResponsiveTable
              caption={t('admin.scraper.failures')}
              rowKey={(f) => f.id}
              rows={failureRows}
              empty={t('common.noResults')}
              columns={[
                { key: 'domain', header: t('admin.scraper.domain'), cell: (f) => f.domain },
                {
                  key: 'url',
                  header: 'URL',
                  cell: (f) => (
                    <span className="line-clamp-1 max-w-xs break-all text-xs">{f.url}</span>
                  ),
                },
                {
                  key: 'error',
                  header: t('admin.scraper.error'),
                  cell: (f) => (
                    <span className="text-xs">
                      {f.errorCode ?? f.status}
                      {f.errorMessage ? ` – ${f.errorMessage}` : ''}
                    </span>
                  ),
                },
                {
                  key: 'strategy',
                  header: t('admin.scraper.strategy'),
                  cell: (f) => f.strategy ?? '–',
                },
                {
                  key: 'created',
                  header: t('common.created'),
                  cell: (f) => formatDate(f.createdAt, i18n.language, true),
                },
              ]}
            />
          )}
          {failures.hasNextPage && (
            <div className="mt-3 text-center">
              <Button
                variant="outline"
                loading={failures.isFetchingNextPage}
                onClick={() => void failures.fetchNextPage()}
              >
                {t('common.loadMore')}
              </Button>
            </div>
          )}
        </section>
      </div>
    </AdminPage>
  );
}
